import { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight, Mail, Phone, MapPin, Clock, Send, Cpu, Bot, TrendingUp, Sparkles, HelpCircle, Instagram, Youtube, Facebook, Twitter, Globe, Building2, Truck, Store, Tractor, Hammer, Package, Sprout, Newspaper } from "lucide-react";
import FAQPage from "./pages/FAQPage";
import AboutPage from "./pages/AboutPage";
import HowItWorksPage from "./pages/HowItWorksPage";
import SalesWorkflowPage from "./pages/SalesWorkflowPage";
import SecurityPage from "./pages/SecurityPage";
import WhyChooseUsPage from "./pages/WhyChooseUsPage";
import GettingStartedPage from "./pages/GettingStartedPage";
import MarketingDistributionPage from "./pages/MarketingDistributionPage";
import AppraisalToMarketPage from "./pages/AppraisalToMarketPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import ValuationPage from "./pages/ValuationPage";
import UsedEquipmentPage from "./pages/UsedEquipmentPage";
import ThreeStepsPage from "./pages/ThreeStepsPage";
import FeaturesPage from "./pages/FeaturesPage";
import PricingPage from "./pages/PricingPage";
import ResourcesPage from "./pages/ResourcesPage";
import SitemapPage from "./pages/SitemapPage";

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { name: "Home", href: "#hero" },
    { name: "Features", href: "/features", isPage: true },
    { name: "Pricing", href: "/pricing", isPage: true },
    { name: "About Us", href: "/about", isPage: true },
    { name: "Resources", href: "/resources", isPage: true },
    { name: "Contact", href: "#contact" },
    { name: "Used Equipment", href: "/used-equipment", isPage: true },
  ];

  const scrollToSection = (e, href, isPage) => {
    e.preventDefault();
    setIsOpen(false);
    if (isPage) {
      window.location.href = href;
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <a href="#hero" onClick={(e) => scrollToSection(e, "#hero")} className="flex items-center gap-2" data-testid="logo-link">
          <img 
            src="https://www.machinerymarketed.com/app-logo.png" 
            alt="MachineryMarketed Logo" 
            className="h-10 w-10"
          />
          <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            <a
              key={item.name}
              href={item.href}
              onClick={(e) => scrollToSection(e, item.href, item.isPage)}
              className="nav-link text-sm font-medium"
              data-testid={`nav-${item.name.toLowerCase()}`}
            >
              {item.name}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => scrollToSection(e, "#contact")}
            className="btn-primary text-sm"
            data-testid="nav-demo-btn"
          >
            Request Demo
          </a>
          <a
            href="https://mach-sales-hub.preview.emergentagent.com/login"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline text-sm px-6 py-2.5 rounded-full"
            style={{ 
              borderColor: "#3b82f6",
              color: "#3b82f6",
              backgroundColor: "transparent"
            }}
            data-testid="nav-signin-btn"
          >
            Sign In
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
          onClick={() => setIsOpen(!isOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden fixed inset-0 top-[72px] z-50 transition-all duration-300 ${
          isOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
        }`}
        style={{ 
          background: "var(--background-100)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)"
        }}
        data-testid="mobile-menu"
      >
        <div className="flex flex-col p-6 gap-2">
          {navItems.map((item, index) => (
            <a
              key={item.name}
              href={item.href}
              onClick={(e) => scrollToSection(e, item.href, item.isPage)}
              className="py-4 px-4 text-lg font-medium rounded-lg hover:bg-white/5 transition-all"
              style={{ 
                color: "var(--text-100)",
                borderBottom: "1px solid rgba(176, 176, 176, 0.15)",
                animationDelay: `${index * 50}ms`
              }}
              data-testid={`mobile-nav-${item.name.toLowerCase()}`}
            >
              {item.name}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => scrollToSection(e, "#contact")}
            className="btn-primary text-center mt-2"
            data-testid="mobile-nav-demo-btn"
          >
            Request Demo
          </a>
          <a
            href="https://mach-sales-hub.preview.emergentagent.com/login"
            target="_blank"
            rel="noopener noreferrer"
            className="text-center mt-2 py-3 px-4 text-lg font-medium rounded-full border-2"
            style={{ 
              borderColor: "#3b82f6",
              color: "#3b82f6",
              backgroundColor: "transparent"
            }}
            data-testid="mobile-nav-signin-btn"
          >
            Sign In
          </a>
        </div>
      </div>
    </nav>
  );
};

// Hero Section - REMOVED floating images
const HeroSection = () => (
  <section id="hero" className="relative flex items-center pt-16 pb-8" style={{ minHeight: "55vh" }} data-testid="hero-section">
    <div className="absolute inset-0 z-0">
      <img
        src="https://www.machinerymarketed.com/hero_excavator.jpg"
        alt="Construction machinery"
        className="w-full h-full object-cover opacity-40"
      />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(0,12,37,0.7), rgba(0,12,37,0.95))" }} />
    </div>
    
    <div className="container mx-auto px-4 lg:px-8 relative z-10">
      <div className="max-w-3xl">
        <h1 
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 leading-tight"
          style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)", wordWrap: "break-word" }}
        >
          Turn Trade-Ins into Cash <span className="text-gradient">In Minutes</span>
        </h1>
        <p className="text-lg md:text-xl mb-3 font-medium" style={{ color: "var(--primary-300)" }}>
          AI Powered for Real Dealers
        </p>
        <p className="text-lg md:text-xl mb-6 leading-relaxed" style={{ color: "var(--text-200)" }}>
          A simple, powerful platform for machinery dealerships: capture trade-ins, 
          set values, and publish listings to your website, marketplaces, and social—in one flow.
        </p>
        <div className="flex flex-wrap gap-4">
          <a href="#contact" className="btn-primary inline-flex items-center gap-2" data-testid="hero-demo-btn">
            Request a demo <ChevronRight size={18} />
          </a>
          <a href="#product" className="btn-outline inline-flex items-center gap-2" data-testid="hero-learn-btn">
            See how it works
          </a>
        </div>
      </div>
    </div>
  </section>
);

// Video Section - Vimeo embeds under hero banner
const VideoSection = () => (
  <section className="pt-0 pb-8" style={{ background: "var(--background-100)" }} data-testid="video-section">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-4">
        {/* Part A - Appraisal Video */}
        <div>
          <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
            <iframe
              src="https://player.vimeo.com/video/1165039791?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
              className="absolute inset-0 w-full h-full rounded-xl shadow-2xl"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              title="MachineryMarketedStageA-TheAppraisal"
            />
          </div>
          <p className="text-center mt-4 text-sm" style={{ color: "var(--text-300)" }}>
            Watch how MachineryMarketed transforms your appraisal process
          </p>
        </div>

        {/* Part B - Valuation Video */}
        <div>
          <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
            <iframe
              src="https://player.vimeo.com/video/1166042303?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
              className="absolute inset-0 w-full h-full rounded-xl shadow-2xl"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              title="PartBValuationVideoForWebsite2"
            />
          </div>
          <p className="text-center mt-4 text-sm" style={{ color: "var(--text-300)" }}>
            Watch how trade valuations are made more accurately and much faster with the valuation process
          </p>
        </div>
      </div>
    </div>
  </section>
);

// AI Banner Section - Updated with #ececf5 background for fanfare
const AIBannerSection = () => (
  <section className="py-12" data-testid="ai-banner-section">
    <div className="container mx-auto px-4 lg:px-8">
      <div 
        className="rounded-2xl p-8 md:p-12 relative overflow-hidden"
        style={{ 
          background: "#ececf5",
          border: "3px solid var(--primary-300)",
          boxShadow: "0 0 40px rgba(15, 95, 255, 0.3)"
        }}
      >
        {/* Shimmer effect */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute top-0 left-0 w-full h-full"
            style={{
              background: "linear-gradient(90deg, transparent, rgba(15, 95, 255, 0.15), transparent)",
              animation: "shimmer 3s infinite"
            }}
          />
        </div>
        
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-2xl" style={{ background: "rgba(15, 95, 255, 0.2)" }}>
                <Sparkles size={40} style={{ color: "var(--primary-200)" }} />
              </div>
              <div>
                <span 
                  className="inline-block px-4 py-1 rounded-full text-sm font-bold mb-2"
                  style={{ background: "var(--primary-200)", color: "white" }}
                >
                  AI-POWERED FEATURES
                </span>
                <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--background-100)" }}>
                  Introducing Our AI Assistants
                </h2>
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: "var(--primary-100)", border: "2px solid var(--primary-200)" }}>
                <Cpu size={28} style={{ color: "white" }} />
                <div>
                  <p className="font-bold" style={{ color: "white" }}>AppraisMacAI</p>
                  <p className="text-sm" style={{ color: "rgba(255,255,255,0.8)" }}>Stage A: Appraisal</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: "var(--primary-200)", border: "2px solid var(--primary-300)" }}>
                <TrendingUp size={28} style={{ color: "white" }} />
                <div>
                  <p className="font-bold" style={{ color: "white" }}>ValueMacAI</p>
                  <p className="text-sm" style={{ color: "rgba(255,255,255,0.8)" }}>Stage B: Valuation</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: "var(--primary-300)", border: "2px solid white" }}>
                <Bot size={28} style={{ color: "white" }} />
                <div>
                  <p className="font-bold" style={{ color: "white" }}>SellMacAI</p>
                  <p className="text-sm" style={{ color: "rgba(255,255,255,0.8)" }}>Stage C: Marketing</p>
                </div>
              </div>
            </div>
          </div>
          
          <p className="text-center mt-6 font-medium" style={{ color: "var(--background-100)" }}>
            Three specialized AI assistants working together to streamline your machinery sales process.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Product Section - Updated "Works Everywhere You Do!"
const ProductSection = () => (
  <section id="product" className="py-20" style={{ background: "var(--background-100)" }} data-testid="product-section">
    <div className="container mx-auto px-4 lg:px-8">
      {/* Turn Trades Into Listings */}
      <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
        <div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Turn Trades <span className="text-gradient">Into Listings</span>
          </h2>
          <p className="text-lg mb-6" style={{ color: "var(--text-200)" }}>
            Capture every trade-in with guided photos, video, and condition notes—then turn it into 
            a polished listing without retyping a word.
          </p>
          <p style={{ color: "var(--accent-100)" }}>
            Used by dealerships selling agriculture, construction, and material handling equipment.
          </p>
        </div>
        <div className="relative">
          <img
            src="https://www.machinerymarketed.com/app-appraisal.jpg"
            alt="App appraisal form"
            className="rounded-2xl shadow-2xl glow-blue"
          />
        </div>
      </div>

      {/* THE DEALERSHIP MOMENT OF TRUTH TEST */}
      <MomentOfTruthTest />

      {/* The Appraisal App - Updated text */}
      <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
        <div className="order-2 lg:order-1">
          <div className="card p-8" style={{ background: "var(--background-200)", borderColor: "rgba(176, 176, 176, 0.2)" }}>
            <span className="badge badge-primary mb-4 text-lg font-bold">WORKS EVERYWHERE YOU DO!</span>
            <p style={{ color: "var(--text-200)" }}>
              Web, PC, Android and iPhone
            </p>
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            The Appraisal App <span className="text-gradient">Built For Dealers</span>
          </h2>
          <p className="text-lg" style={{ color: "var(--text-200)" }}>
            A step-by-step workflow your sales team can use on the lot—so every trade-in is 
            documented, valued, and ready to market before the customer leaves.
          </p>
        </div>
      </div>

      {/* Appraise. Value. Market. */}
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
          Appraise. Value. Market.
        </h2>
        <p style={{ color: "var(--text-200)" }}>One platform. Three steps. Zero duplicate work.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {[
          {
            step: "1",
            title: "Appraise",
            desc: "Guided capture: photos, video, condition, and customer expectations. Everything your valuation team needs in one place."
          },
          {
            step: "2",
            title: "Value",
            desc: "Set market value, margin, and reconditioning costs—then lock the trade. Real-time calculations with every deduction."
          },
          {
            step: "3",
            title: "Market",
            desc: "Publish to your site, marketplaces, social, and buyer lists—in one click. Reach thousands of potential buyers instantly."
          }
        ].map((item) => (
          <div key={item.step} className="card p-8 text-center" style={{ background: "var(--background-200)" }}>
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ background: "linear-gradient(135deg, var(--primary-200), var(--primary-300))" }}
            >
              <span className="text-2xl font-bold" style={{ color: "var(--text-100)" }}>{item.step}</span>
            </div>
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
              {item.title}
            </h3>
            <p style={{ color: "var(--text-200)" }}>{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// AI Features Section
const AIFeaturesSection = () => (
  <section id="ai" className="py-20" style={{ background: "var(--background-200)" }} data-testid="ai-section">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="text-center mb-16">
        <span className="badge badge-primary mb-4">Powered by AI</span>
        <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
          Smart Technology. <span className="text-gradient">Better Results.</span>
        </h2>
        <p className="max-w-2xl mx-auto" style={{ color: "var(--text-200)" }}>
          Our AI helps you capture the right details, create professional listings, 
          and find the perfect buyers for every machine.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          {
            title: "Guided Data Capture",
            desc: "AI prompts ensure you get all the right details, quickly and professionally into one comprehensive report."
          },
          {
            title: "Customizable Listings",
            desc: "AI generates professional, customizable listings tailored to each machine and your dealership's brand."
          },
          {
            title: "Smart Buyer Targeting",
            desc: "AI identifies and targets the right buyers for each machine type, maximizing your reach and response rates."
          },
          {
            title: "Accurate Marketplace Data",
            desc: "AI ensures all information pushed to marketplaces is accurate, complete, and optimized for maximum visibility."
          }
        ].map((item, index) => (
          <div key={index} className="card p-6" style={{ background: "var(--background-300)", borderColor: "rgba(176, 176, 176, 0.15)" }}>
            <h3 className="text-xl font-bold mb-3" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
              {item.title}
            </h3>
            <p className="text-sm" style={{ color: "var(--text-300)" }}>{item.desc}</p>
          </div>
        ))}
      </div>

      {/* Trade Value Calculator */}
      <div className="grid lg:grid-cols-2 gap-12 items-center mt-24">
        <div>
          <span className="badge badge-accent mb-4">Secure Internal Tool</span>
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Trade Value <span className="text-gradient">Calculator</span>
          </h2>
          <p className="text-lg mb-6" style={{ color: "var(--text-200)" }}>
            Make confident valuation decisions with our secure internal calculator. Sales teams 
            and managers can quickly assess trade-in values with real-time market data, margin 
            analysis, and reconditioning costs—all in one place.
          </p>
          <div className="flex flex-wrap gap-4">
            <span className="badge badge-primary">Real-time Data</span>
            <span className="badge badge-primary">Margin Analysis</span>
            <span className="badge badge-primary">Secure Access</span>
          </div>
          <p className="mt-4 text-sm" style={{ color: "var(--accent-100)" }}>
            Role-based access for sales teams and management.
          </p>
        </div>
        <div>
          <img
            src="https://www.machinerymarketed.com/app-valuation.jpg"
            alt="Trade value calculator"
            className="rounded-2xl shadow-2xl glow-blue"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  </section>
);

// Visual Showcase Section - Breaking up text-heavy content
const VisualShowcaseSection = () => (
  <section className="py-16" style={{ background: "var(--background-200)" }} data-testid="visual-showcase-section">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
          See It In <span className="text-gradient">Action</span>
        </h2>
        <p style={{ color: "var(--text-200)" }}>
          Real dealerships, real results, real machinery moving faster.
        </p>
      </div>
      
      <div className="grid md:grid-cols-3 gap-6">
        {/* Image 1 */}
        <div className="card overflow-hidden group">
          <div className="aspect-[4/3] overflow-hidden">
            <img
              src="https://www.machinerymarketed.com/agri-1.jpg"
              alt="Equipment in field"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
            />
          </div>
          <div className="p-4">
            <p className="font-semibold" style={{ color: "var(--text-100)" }}>Capture on the go</p>
            <p className="text-sm" style={{ color: "var(--text-300)" }}>Any device, any location</p>
          </div>
        </div>
        
        {/* Image 2 */}
        <div className="card overflow-hidden group">
          <div className="aspect-[4/3] overflow-hidden">
            <img
              src="https://www.machinerymarketed.com/loader_yard.jpg"
              alt="Equipment at dealership"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
            />
          </div>
          <div className="p-4">
            <p className="font-semibold" style={{ color: "var(--text-100)" }}>Professional listings</p>
            <p className="text-sm" style={{ color: "var(--text-300)" }}>Published in minutes</p>
          </div>
        </div>
        
        {/* Image 3 */}
        <div className="card overflow-hidden group">
          <div className="aspect-[4/3] overflow-hidden">
            <img
              src="https://www.machinerymarketed.com/tractor_loader_pair.jpg"
              alt="Multiple machines"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
            />
          </div>
          <div className="p-4">
            <p className="font-semibold" style={{ color: "var(--text-100)" }}>Multi-channel reach</p>
            <p className="text-sm" style={{ color: "var(--text-300)" }}>One click to everywhere</p>
          </div>
        </div>
      </div>
      
      {/* Stats Bar */}
      <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="text-center p-4 rounded-xl" style={{ background: "var(--background-300)" }}>
          <p className="text-2xl font-bold" style={{ color: "var(--primary-300)" }}>10-15</p>
          <p className="text-sm" style={{ color: "var(--text-300)" }}>Minutes to appraise</p>
        </div>
        <div className="text-center p-4 rounded-xl" style={{ background: "var(--background-300)" }}>
          <p className="text-2xl font-bold" style={{ color: "var(--primary-300)" }}>7+</p>
          <p className="text-sm" style={{ color: "var(--text-300)" }}>Marketplaces</p>
        </div>
        <div className="text-center p-4 rounded-xl" style={{ background: "var(--background-300)" }}>
          <p className="text-2xl font-bold" style={{ color: "var(--primary-300)" }}>1 Click</p>
          <p className="text-sm" style={{ color: "var(--text-300)" }}>To publish everywhere</p>
        </div>
        <div className="text-center p-4 rounded-xl" style={{ background: "var(--background-300)" }}>
          <p className="text-2xl font-bold" style={{ color: "var(--primary-300)" }}>24/7</p>
          <p className="text-sm" style={{ color: "var(--text-300)" }}>Cloud backed up</p>
        </div>
      </div>
    </div>
  </section>
);

// Features Grid Section - Using background images style
const FeaturesGridSection = () => (
  <section className="py-20" style={{ background: "var(--background-100)" }} data-testid="features-section">
    <div className="container mx-auto px-4 lg:px-8">
      {/* Built For The Yard */}
      <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
        <div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Built For The Yard. <span className="text-gradient">Not The Office.</span>
          </h2>
          <p className="text-lg" style={{ color: "var(--text-200)" }}>
            Sunlight, dust, and gloves-friendly. Capture what matters without spreadsheets or paperwork.
          </p>
        </div>
        <div 
          className="relative rounded-2xl overflow-hidden h-80"
          style={{ 
            backgroundImage: "url('https://www.machinerymarketed.com/excavator_ground.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <span className="badge badge-primary mb-2">Offline-First</span>
            <p style={{ color: "var(--text-200)" }}>
              Offline-first saves drafts; syncs when you're back in range.
            </p>
          </div>
        </div>
      </div>

      {/* Works Where Your Machines Work */}
      <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
        <div 
          className="relative rounded-2xl overflow-hidden h-80"
          style={{ 
            backgroundImage: "url('https://www.machinerymarketed.com/tractor_loader_pair.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        </div>
        <div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Works Where Your <span className="text-gradient">Machines Work</span>
          </h2>
          <p className="text-lg mb-6" style={{ color: "var(--text-200)" }}>
            Rugged, reliable, and ready for the real world—so your team can capture details 
            on the spot and keep deals moving.
          </p>
          <div className="flex items-center gap-3">
            <span className="badge badge-accent">Cloud-Backed</span>
            <span style={{ color: "var(--text-300)" }}>Cloud-backed with local draft storage.</span>
          </div>
        </div>
      </div>

      {/* Your Next Buyer */}
      <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
        <div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Your Next Buyer <span className="text-gradient">Is Already Looking</span>
          </h2>
          <p className="text-lg mb-6" style={{ color: "var(--text-200)" }}>
            Get your trade-ins in front of more buyers—faster. Our distribution network 
            puts listings where buyers actually search.
          </p>
          <div className="flex items-center gap-3">
            <span className="badge badge-primary">Multi-Channel</span>
            <span style={{ color: "var(--text-300)" }}>Marketplaces + social + direct buyer lists.</span>
          </div>
        </div>
        <div 
          className="relative rounded-2xl overflow-hidden h-80"
          style={{ 
            backgroundImage: "url('https://www.machinerymarketed.com/agri-7.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        </div>
      </div>

      {/* Built For Dealerships */}
      <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
        <div 
          className="relative rounded-2xl overflow-hidden h-80"
          style={{ 
            backgroundImage: "url('https://www.machinerymarketed.com/agri-4.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        </div>
        <div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Built For Dealerships <span className="text-gradient">That Move Metal</span>
          </h2>
          <p className="text-lg mb-6" style={{ color: "var(--text-200)" }}>
            We're not a generic listing tool. We're built for machinery dealers—by people 
            who know the yard, the sales floor, and the close.
          </p>
          <div className="flex items-center gap-3">
            <span className="badge badge-accent">Industry Experts</span>
            <span style={{ color: "var(--text-300)" }}>Support that speaks your language.</span>
          </div>
        </div>
      </div>

      {/* Upload Once */}
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Upload Once. <span className="text-gradient">Publish Everywhere.</span>
          </h2>
          <p className="text-lg mb-6" style={{ color: "var(--text-200)" }}>
            One listing. Multiple channels. Updated instantly, managed from one place.
          </p>
          
          {/* Our Platforms */}
          <div className="mb-6">
            <p className="text-sm font-semibold mb-3" style={{ color: "var(--accent-100)" }}>
              We publish to our platforms:
            </p>
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: "var(--background-200)" }}>
                <Globe size={18} style={{ color: "var(--primary-300)" }} />
                <span className="text-sm">Our Website</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: "var(--background-200)" }}>
                <Instagram size={18} style={{ color: "#E4405F" }} />
                <span className="text-sm">Instagram</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: "var(--background-200)" }}>
                <Facebook size={18} style={{ color: "#1877F2" }} />
                <span className="text-sm">Facebook</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: "var(--background-200)" }}>
                <Twitter size={18} style={{ color: "#1DA1F2" }} />
                <span className="text-sm">X</span>
              </div>
            </div>
          </div>
          
          {/* Dealer Platforms */}
          <div className="mb-6">
            <p className="text-sm font-semibold mb-3" style={{ color: "var(--accent-100)" }}>
              Plus your dealer's channels:
            </p>
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: "var(--background-200)" }}>
                <Building2 size={18} style={{ color: "var(--primary-300)" }} />
                <span className="text-sm">Dealer Website</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: "var(--background-200)" }}>
                <Instagram size={18} style={{ color: "#E4405F" }} />
                <span className="text-sm">Dealer Instagram</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: "var(--background-200)" }}>
                <Facebook size={18} style={{ color: "#1877F2" }} />
                <span className="text-sm">Dealer Facebook</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: "var(--background-200)" }}>
                <Twitter size={18} style={{ color: "#1DA1F2" }} />
                <span className="text-sm">Dealer X</span>
              </div>
            </div>
          </div>
          
          {/* Marketplace Icons */}
          <div className="mb-6">
            <p className="text-sm font-semibold mb-3" style={{ color: "var(--accent-100)" }}>
              Major marketplace platforms:
            </p>
            <div className="grid grid-cols-4 md:grid-cols-7 gap-3">
              {/* Machines4U */}
              <div className="flex flex-col items-center gap-1 p-2 rounded-lg" style={{ background: "var(--background-200)" }}>
                <Truck size={24} style={{ color: "var(--primary-300)" }} />
                <span className="text-xs text-center">Machines4U</span>
              </div>
              {/* Machinery Trader */}
              <div className="flex flex-col items-center gap-1 p-2 rounded-lg" style={{ background: "var(--background-200)" }}>
                <Store size={24} style={{ color: "var(--primary-300)" }} />
                <span className="text-xs text-center">MachineryTrader</span>
              </div>
              {/* Farm Machinery Sales */}
              <div className="flex flex-col items-center gap-1 p-2 rounded-lg" style={{ background: "var(--background-200)" }}>
                <Tractor size={24} style={{ color: "var(--primary-300)" }} />
                <span className="text-xs text-center">FarmMachinery</span>
              </div>
              {/* Construction Sales */}
              <div className="flex flex-col items-center gap-1 p-2 rounded-lg" style={{ background: "var(--background-200)" }}>
                <Hammer size={24} style={{ color: "var(--primary-300)" }} />
                <span className="text-xs text-center">Construction</span>
              </div>
              {/* Used Machinery Sales */}
              <div className="flex flex-col items-center gap-1 p-2 rounded-lg" style={{ background: "var(--background-200)" }}>
                <Package size={24} style={{ color: "var(--primary-300)" }} />
                <span className="text-xs text-center">UsedMachinery</span>
              </div>
              {/* Farm Tender */}
              <div className="flex flex-col items-center gap-1 p-2 rounded-lg" style={{ background: "var(--background-200)" }}>
                <Sprout size={24} style={{ color: "var(--primary-300)" }} />
                <span className="text-xs text-center">FarmTender</span>
              </div>
              {/* Trading Post */}
              <div className="flex flex-col items-center gap-1 p-2 rounded-lg" style={{ background: "var(--background-200)" }}>
                <Newspaper size={24} style={{ color: "var(--primary-300)" }} />
                <span className="text-xs text-center">TradingPost</span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4">
            <span className="badge badge-accent">Your Website</span>
            <span className="badge badge-accent">Your Socials</span>
            <span className="badge badge-accent">Your Database</span>
          </div>
        </div>
        <div>
          <img
            src="https://www.machinerymarketed.com/app-machine-details.jpg"
            alt="App report view"
            className="rounded-2xl shadow-2xl glow-blue"
            loading="lazy"
          />
          <p className="mt-4 text-center text-sm" style={{ color: "var(--accent-100)" }}>
            Includes autosyndication and link tracking.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Equipment Section
const EquipmentSection = () => {
  const equipment = [
    {
      image: "https://www.machinerymarketed.com/agri-1.jpg",
      title: "2021 John Deere 6150R",
      specs: "150HP • 2,400 hrs",
      price: "$185,000"
    },
    {
      image: "https://www.machinerymarketed.com/excavator_ground.jpg",
      title: "2019 CAT 320GC",
      specs: "21T • 3,100 hrs",
      price: "$245,000"
    },
    {
      image: "https://www.machinerymarketed.com/agri-6.jpg",
      title: "2020 Case IH 9240",
      specs: "550HP • 1,800 hrs",
      price: "$520,000"
    },
    {
      image: "https://www.machinerymarketed.com/agri-3.jpg",
      title: "2022 Komatsu WA380",
      specs: "18T • 950 hrs",
      price: "$195,000"
    }
  ];

  return (
    <section id="equipment" className="py-20" style={{ background: "var(--background-200)" }} data-testid="equipment-section">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <span className="badge badge-accent mb-4">Subscriber Listings</span>
          <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Equipment For Sale
          </h2>
          <p style={{ color: "var(--text-200)" }}>
            Browse quality machinery from our subscriber dealerships. All listings are professionally appraised and market-ready.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {equipment.map((item, index) => (
            <div key={index} className="equipment-card" data-testid={`equipment-card-${index}`}>
              <div className="aspect-video overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover img-hover"
                  loading="lazy"
                />
              </div>
              <div className="p-5">
                <h3 className="font-bold text-lg mb-2" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                  {item.title}
                </h3>
                <p className="text-sm mb-3" style={{ color: "var(--accent-100)" }}>{item.specs}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold" style={{ color: "var(--primary-300)" }}>{item.price}</span>
                  <button className="btn-primary text-sm py-2 px-4">View</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <button className="btn-outline">View All Equipment</button>
        </div>
      </div>
    </section>
  );
};

// Media Section
const MediaSection = () => (
  <section id="media" className="py-20" style={{ background: "var(--background-100)" }} data-testid="media-section">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-bold" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
          From Our Socials
        </h2>
      </div>
      
      <div className="flex flex-wrap justify-center gap-6">
        {/* Instagram */}
        <a
          href="https://www.instagram.com/machinery.marketed"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-6 py-4 rounded-xl transition-all hover:scale-105"
          style={{ background: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)" }}
        >
          <Instagram size={28} style={{ color: "white" }} />
          <span className="font-semibold text-white">Instagram</span>
        </a>
        
        {/* YouTube */}
        <a
          href="https://youtube.com/@machinerymarketed?si=EpZOfUQ25fCIQlJ7"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-6 py-4 rounded-xl transition-all hover:scale-105"
          style={{ backgroundColor: "#FF0000" }}
        >
          <Youtube size={28} style={{ color: "white" }} />
          <span className="font-semibold text-white">YouTube</span>
        </a>
        
        {/* Facebook */}
        <a
          href="https://facebook.com/profile.php?id=61587527264958"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-6 py-4 rounded-xl transition-all hover:scale-105"
          style={{ backgroundColor: "#1877F2" }}
        >
          <Facebook size={28} style={{ color: "white" }} />
          <span className="font-semibold text-white">Facebook</span>
        </a>
      </div>
    </div>
  </section>
);

// Coming Soon Section
const ComingSoonSection = () => (
  <section className="py-20" style={{ background: "var(--background-200)" }} data-testid="coming-soon-section">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-bold" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
          Coming Soon
        </h2>
      </div>

      {/* AI Features Banner at top of Coming Soon - with #ececf5 background */}
      <div 
        className="mb-12 rounded-2xl p-8 text-center relative overflow-hidden"
        style={{ 
          background: "#ececf5",
          border: "3px solid var(--primary-300)",
          boxShadow: "0 0 40px rgba(15, 95, 255, 0.3)"
        }}
      >
        <div className="relative z-10">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles size={32} style={{ color: "var(--primary-200)" }} />
            <span 
              className="inline-block px-4 py-2 rounded-full text-lg font-bold"
              style={{ background: "var(--primary-200)", color: "white" }}
            >
              28th February 2026
            </span>
          </div>
          <h3 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--background-100)" }}>
            AI-Powered Support Functions
          </h3>
          <p className="mb-6 max-w-2xl mx-auto" style={{ color: "var(--background-200)" }}>
            Three specialized AI assistants will be complete and included in the app:
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg" style={{ background: "var(--primary-100)" }}>
              <Cpu size={20} style={{ color: "white" }} />
              <span className="font-semibold" style={{ color: "white" }}>AppraisMacAI</span>
              <span className="text-sm" style={{ color: "rgba(255,255,255,0.8)" }}>(Appraisal)</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg" style={{ background: "var(--primary-200)" }}>
              <TrendingUp size={20} style={{ color: "white" }} />
              <span className="font-semibold" style={{ color: "white" }}>ValueMacAI</span>
              <span className="text-sm" style={{ color: "rgba(255,255,255,0.8)" }}>(Valuation)</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg" style={{ background: "var(--primary-300)" }}>
              <Bot size={20} style={{ color: "white" }} />
              <span className="font-semibold" style={{ color: "white" }}>SellMacAI</span>
              <span className="text-sm" style={{ color: "rgba(255,255,255,0.8)" }}>(Marketing)</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-12 items-start">
        <div className="space-y-8">
          {[
            {
              quarter: "Q2 2026",
              title: "MachineryMarketed Pro",
              desc: "Advanced analytics, CRM integration, and multi-location support for larger dealership groups."
            },
            {
              quarter: "Q3 2026",
              title: "Mobile App v2.0",
              desc: "Complete redesign with faster capture, offline mode improvements, and voice-to-text notes."
            },
            {
              quarter: "Q4 2026",
              title: "Auction Module",
              desc: "Run your own timed auctions directly from the platform with live bidding and auto-notifications."
            }
          ].map((item, index) => (
            <div key={index} className="timeline-item pl-8 pb-8">
              <span className="badge badge-primary mb-2">{item.quarter}</span>
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                {item.title}
              </h3>
              <p style={{ color: "var(--text-200)" }}>{item.desc}</p>
            </div>
          ))}
        </div>
        <div>
          <img
            src="https://www.machinerymarketed.com/app-create-account.jpg"
            alt="App dashboard"
            className="rounded-2xl shadow-2xl glow-blue"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  </section>
);

// CTA Section - Updated email
const CTASection = () => (
  <section className="py-20" style={{ background: "var(--background-300)" }} data-testid="cta-section">
    <div className="container mx-auto px-4 lg:px-8 text-center">
      <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
        Ready To Move <span className="text-gradient">Your Metal Faster?</span>
      </h2>
      <p className="text-lg mb-8 max-w-2xl mx-auto" style={{ color: "var(--text-200)" }}>
        Join dealerships turning trade-ins into listings in minutes—not days. Let's talk about your workflow.
      </p>
      <div className="flex flex-wrap justify-center gap-4 mb-6">
        <a href="#contact" className="btn-primary inline-flex items-center gap-2">
          Request a demo <ChevronRight size={18} />
        </a>
        <a href="mailto:control@machinerymarketed.com" className="btn-outline">
          Or email control@machinerymarketed.com
        </a>
      </div>
    </div>
  </section>
);

// Contact Section - Supports both Formspree and Custom API
const ContactSection = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    dealership: "",
    email: "",
    message: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Check if Formspree is configured (easiest option)
      const formspreeEndpoint = process.env.REACT_APP_FORMSPREE_ENDPOINT;
      
      if (formspreeEndpoint) {
        // Option 1: Use Formspree (no backend needed)
        const response = await fetch(formspreeEndpoint, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...formData,
            _subject: `New Contact Form from ${formData.name}`,
            _replyto: formData.email,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to submit form. Please try again.');
        }

        setIsSubmitted(true);
        setFormData({ name: "", dealership: "", email: "", message: "" });
      } else {
        // Option 2: Use custom backend API
        const apiUrl = process.env.REACT_APP_API_URL || '/api';
        
        const response = await fetch(`${apiUrl}/contact`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        if (!response.ok) {
          throw new Error('Failed to submit form. Please try again.');
        }

        const result = await response.json();
        
        if (result.success) {
          setIsSubmitted(true);
          setFormData({ name: "", dealership: "", email: "", message: "" });
        } else {
          throw new Error(result.message || 'Something went wrong.');
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20" style={{ background: "var(--background-100)" }} data-testid="contact-section">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Let's Talk.
          </h2>
          <p style={{ color: "var(--text-200)" }}>
            Tell us what you're selling and how your team works. We'll show you a setup that fits.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg" style={{ background: "var(--background-200)" }}>
                <Mail size={24} style={{ color: "var(--primary-300)" }} />
              </div>
              <div>
                <h4 className="font-bold mb-1" style={{ color: "var(--text-100)" }}>Email</h4>
                <a href="mailto:control@machinerymarketed.com" className="footer-link">
                  control@machinerymarketed.com
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg" style={{ background: "var(--background-200)" }}>
                <Phone size={24} style={{ color: "var(--primary-300)" }} />
              </div>
              <div>
                <h4 className="font-bold mb-1" style={{ color: "var(--text-100)" }}>Phone</h4>
                <a href="tel:+61497085157" className="footer-link">
                  +61 497 085 157
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg" style={{ background: "var(--background-200)" }}>
                <Clock size={24} style={{ color: "var(--primary-300)" }} />
              </div>
              <div>
                <h4 className="font-bold mb-1" style={{ color: "var(--text-100)" }}>Hours</h4>
                <p style={{ color: "var(--text-300)" }}>Mon–Fri, 8am–6pm AEST</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg" style={{ background: "var(--background-200)" }}>
                <MapPin size={24} style={{ color: "var(--primary-300)" }} />
              </div>
              <div>
                <h4 className="font-bold mb-1" style={{ color: "var(--text-100)" }}>Location</h4>
                <p style={{ color: "var(--text-300)" }}>Australia & North America</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="card p-8" style={{ background: "var(--background-200)", borderColor: "rgba(176, 176, 176, 0.15)" }}>
            {!isSubmitted ? (
              <>
                <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                  Send us a message
                </h3>
                
                {error && (
                  <div 
                    className="mb-4 p-4 rounded-lg"
                    style={{ background: "rgba(255, 100, 100, 0.2)", color: "#ff6464", border: "1px solid #ff6464" }}
                  >
                    {error}
                  </div>
                )}
                
                <form 
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Name"
                      className="w-full"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="dealership"
                      placeholder="Dealership"
                      className="w-full"
                      value={formData.dealership}
                      onChange={handleChange}
                      data-testid="contact-dealership-input"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Email"
                      className="w-full"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="Message"
                      className="w-full h-32 resize-none"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      data-testid="contact-message-input"
                    />
                  </div>
                  <button 
                    type="submit" 
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={18} /> Send message
                      </>
                    )}
                  </button>
                </form>
              </>
            ) : (
              /* Success Message */
              <div className="text-center py-8">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                  style={{ background: "var(--primary-100)" }}
                >
                  <Sparkles size={32} style={{ color: "white" }} />
                </div>
                <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                  Thanks for reaching out!
                </h3>
                <p style={{ color: "var(--text-200)" }}>
                  We've received your message and will be in touch very soon to show you what our app can do.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer - Added blazing arrow
const Footer = ({ onNavigate }) => (
  <footer className="py-12" style={{ background: "var(--background-200)", borderTop: "1px solid rgba(176, 176, 176, 0.1)" }} data-testid="footer">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <img 
            src="https://www.machinerymarketed.com/app-logo.png" 
            alt="MachineryMarketed Logo" 
            className="h-10 w-10"
          />
          <span className="font-bold text-lg" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </div>

        <div className="flex items-center gap-6">
          <Link to="/privacy" className="footer-link text-sm" data-testid="footer-privacy-link">Privacy</Link>
          <Link to="/terms" className="footer-link text-sm" data-testid="footer-terms-link">Terms</Link>
          <Link to="/cookies" className="footer-link text-sm" data-testid="footer-cookies-link">Cookies</Link>
          <Link to="/faq" className="footer-link text-sm">FAQ</Link>
        </div>

        <div className="flex items-center gap-4">
          {/* Create Account Button */}
          <a
            href="https://mach-sales-hub.preview.emergentagent.com/register"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium px-6 py-2.5 rounded-full border-2 transition-all hover:opacity-90"
            style={{ 
              borderColor: "#3b82f6",
              color: "#3b82f6",
              backgroundColor: "transparent"
            }}
          >
            Create Account
          </a>
          <p className="text-sm" style={{ color: "var(--text-300)" }}>
            © 2026 MachineryMarketed. All rights reserved.
          </p>
        </div>
      </div>
      
      {/* Social Media Links */}
      <div className="mt-6 flex justify-center gap-4">
        <a
          href="https://www.instagram.com/machinery.marketed"
          target="_blank"
          rel="noopener noreferrer"
          className="p-3 rounded-full transition-all hover:scale-110"
          style={{ background: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)" }}
          aria-label="Instagram"
        >
          <Instagram size={20} style={{ color: "white" }} />
        </a>
        <a
          href="https://youtube.com/@machinerymarketed?si=EpZOfUQ25fCIQlJ7"
          target="_blank"
          rel="noopener noreferrer"
          className="p-3 rounded-full transition-all hover:scale-110"
          style={{ backgroundColor: "#FF0000" }}
          aria-label="YouTube"
        >
          <Youtube size={20} style={{ color: "white" }} />
        </a>
        <a
          href="https://facebook.com/profile.php?id=61587527264958"
          target="_blank"
          rel="noopener noreferrer"
          className="p-3 rounded-full transition-all hover:scale-110"
          style={{ backgroundColor: "#1877F2" }}
          aria-label="Facebook"
        >
          <Facebook size={20} style={{ color: "white" }} />
        </a>
      </div>
      
      {/* Blazing Arrow - Rotated 90deg clockwise (pointing right) */}
      <div className="mt-8 flex justify-center">
        <img 
          src="https://www.machinerymarketed.com/blazing-arrow.jpg" 
          alt="Blazing Arrow" 
          className="h-24 w-auto opacity-80"
          style={{ 
            filter: "drop-shadow(0 0 10px rgba(255, 100, 50, 0.5))",
            transform: "rotate(90deg)"
          }}
          loading="lazy"
        />
      </div>
      
      {/* HTTPS Security Badge */}
      <div className="mt-6 flex justify-center">
        <div 
          className="flex items-center gap-2 px-4 py-2 rounded-full"
          style={{ 
            background: "rgba(34, 197, 94, 0.15)",
            border: "1px solid rgba(34, 197, 94, 0.3)"
          }}
        >
          <svg 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            style={{ color: "#22c55e" }}
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
          <span className="text-sm font-medium" style={{ color: "#22c55e" }}>
            Secure HTTPS Connection
          </span>
        </div>
      </div>
    </div>
  </footer>
);

// Moment of Truth Test Component
const MomentOfTruthTest = () => {
  const [listed, setListed] = useState('');
  const [total, setTotal] = useState('');
  const [result, setResult] = useState(null);

  const calculatePercentage = () => {
    const listedNum = parseInt(listed) || 0;
    const totalNum = parseInt(total) || 0;
    if (totalNum > 0) {
      const percentage = Math.round((listedNum / totalNum) * 100);
      setResult(percentage);
    }
  };

  const getMessage = (percentage) => {
    if (percentage >= 90) return { text: "IMPRESSIVE! But we can still help you scale.", color: "#22c55e" };
    if (percentage >= 70) return { text: "GOOD... BUT YOU'RE LEAVING MONEY ON THE TABLE!", color: "#f59e0b" };
    if (percentage >= 50) return { text: "OUCH! THAT'S A LOT OF HIDDEN INVENTORY.", color: "#f97316" };
    return { text: "CRITICAL! YOU NEED OUR APP RIGHT NOW!", color: "#ef4444" };
  };

  const message = result !== null ? getMessage(result) : null;

  return (
    <div className="mb-24 relative overflow-hidden rounded-2xl" style={{ background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 50%, #7f1d1d 100%)" }}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)", backgroundSize: "60px 60px" }} />
      </div>
      
      <div className="relative z-10 p-8 md:p-12 lg:p-16">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold mb-4" style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "2px solid rgba(255,255,255,0.3)" }}>
            <span className="text-xl">⚡</span>
            REALITY CHECK
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4 tracking-tight" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "white", textShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>
            THE DEALERSHIP<br />
            <span style={{ color: "#fecaca" }}>MOMENT OF TRUTH TEST</span>
          </h2>
          <p className="text-lg md:text-xl max-w-3xl mx-auto" style={{ color: "rgba(255,255,255,0.9)" }}>
            We're confident our app will transform your operation. We know it works, we know its real value for money, 
            and we know it shines a bright light on areas where <strong>profit leakage kills your bottom line</strong>. 
            But we've got to convince YOU—the Dealer—that you need us.
          </p>
          <p className="text-2xl font-bold mt-4" style={{ color: "#fecaca" }}>
            So... here's a little test. Be honest... 👇
          </p>
        </div>

        {/* Calculator Inputs */}
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Listed Online Input */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border-2 border-white/20">
              <label className="block text-sm font-bold mb-3" style={{ color: "rgba(255,255,255,0.8)" }}>
                Input below the No. of Trades you have listed online
              </label>
              <input
                type="number"
                min="0"
                value={listed}
                onChange={(e) => setListed(e.target.value)}
                placeholder="0"
                className="w-full px-4 py-4 rounded-lg text-3xl font-black text-center bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-white/50"
                style={{ fontFamily: "'Smooch Sans', sans-serif" }}
              />
            </div>

            {/* Total Stock Input */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border-2 border-white/20">
              <label className="block text-sm font-bold mb-3" style={{ color: "rgba(255,255,255,0.8)" }}>
                Input below the No. of Trades you have in stock, total
              </label>
              <input
                type="number"
                min="0"
                value={total}
                onChange={(e) => setTotal(e.target.value)}
                placeholder="0"
                className="w-full px-4 py-4 rounded-lg text-3xl font-black text-center bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-white/50"
                style={{ fontFamily: "'Smooch Sans', sans-serif" }}
              />
            </div>
          </div>

          {/* Calculate Button */}
          <div className="text-center mb-8">
            <button
              onClick={calculatePercentage}
              disabled={!listed || !total}
              className="px-10 py-4 rounded-lg font-black text-xl transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed"
              style={{ 
                background: "white", 
                color: "#dc2626",
                boxShadow: "0 10px 40px rgba(0,0,0,0.3)"
              }}
            >
              🔍 REVEAL THE TRUTH
            </button>
          </div>

          {/* Result Display */}
          {result !== null && (
            <div 
              className="rounded-xl p-8 text-center animate-pulse"
              style={{ 
                background: "rgba(0,0,0,0.3)",
                border: `4px solid ${message.color}`,
                boxShadow: `0 0 40px ${message.color}40`
              }}
            >
              <div className="text-7xl md:text-8xl font-black mb-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: message.color, textShadow: `0 0 20px ${message.color}60` }}>
                {result}%
              </div>
              <div className="text-2xl md:text-3xl font-black uppercase tracking-wide" style={{ color: message.color }}>
                OF TRADES LISTED ONLINE
              </div>
              <div className="mt-4 text-xl md:text-2xl font-bold" style={{ color: "white" }}>
                {message.text}
              </div>
              {result < 90 && (
                <div className="mt-6">
                  <a 
                    href="#contact"
                    className="inline-block px-8 py-3 rounded-lg font-bold text-lg transition-all hover:scale-105"
                    style={{ background: message.color, color: "white" }}
                  >
                    Get MachineryMarketed Now →
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom Message */}
        <div className="mt-10 text-center">
          <p className="text-sm" style={{ color: "rgba(255,255,255,0.6)" }}>
            💡 Every machine sitting in your yard unlisted is costing you money. 
            The average dealer has 30-40% of trades invisible to buyers.
          </p>
        </div>
      </div>
    </div>
  );
};

// Home Page Component
const HomePage = () => (
  <>
    <Navigation />
    <main>
      <HeroSection />
      <VideoSection />
      <AIBannerSection />
      <ProductSection />
      <AIFeaturesSection />
      <VisualShowcaseSection />
      <FeaturesGridSection />
      <EquipmentSection />
      <MediaSection />
      <ComingSoonSection />
      <CTASection />
      <ContactSection />
    </main>
    <Footer />
  </>
);

// Privacy Policy Page - Updated email
const PrivacyPage = () => (
  <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
    <nav className="glass py-4 sticky top-0 z-50">
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src="https://www.machinerymarketed.com/app-logo.png" alt="Logo" className="h-10 w-10" />
          <span className="font-bold text-xl" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <Link to="/" className="btn-outline text-sm">Back to Home</Link>
      </div>
    </nav>
    
    <main className="container mx-auto px-4 lg:px-8 py-16" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold mb-8" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
          Privacy Policy
        </h1>
        
        <div className="card p-8 space-y-6" style={{ background: "var(--background-200)", borderColor: "rgba(176, 176, 176, 0.15)" }}>
          <p className="text-sm" style={{ color: "var(--accent-100)" }}>(Current Status)</p>
          <p style={{ color: "var(--text-300)" }}>Last updated: February 2026</p>
          
          <p style={{ color: "var(--text-200)" }}>
            Machinery Marketed! ("we", "us", "our") respects the privacy of visitors, users, and partners. 
            This Privacy Policy explains, at a high level, how information is handled while the platform 
            and associated tools are in active development and early deployment.
          </p>

          <h2 className="text-2xl font-bold pt-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Information We May Collect
          </h2>
          <p style={{ color: "var(--text-200)" }}>We may collect limited information when you:</p>
          <ul className="list-disc list-inside space-y-2" style={{ color: "var(--text-200)" }}>
            <li>Visit our website</li>
            <li>Submit a contact or enquiry form</li>
            <li>Interact with demo or early-access features</li>
            <li>Communicate with us directly</li>
          </ul>
          <p style={{ color: "var(--text-200)" }}>This information may include:</p>
          <ul className="list-disc list-inside space-y-2" style={{ color: "var(--text-200)" }}>
            <li>Name, business name, and contact details</li>
            <li>Device, browser, and basic usage data</li>
            <li>Content voluntarily submitted by you</li>
          </ul>

          <h2 className="text-2xl font-bold pt-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            How Information Is Used
          </h2>
          <p style={{ color: "var(--text-200)" }}>Information is used solely to:</p>
          <ul className="list-disc list-inside space-y-2" style={{ color: "var(--text-200)" }}>
            <li>Respond to enquiries and requests</li>
            <li>Improve platform functionality and user experience</li>
            <li>Support onboarding, demonstrations, and early access</li>
            <li>Maintain site performance, security, and integrity</li>
          </ul>
          <p className="font-semibold" style={{ color: "var(--text-100)" }}>
            We do not sell personal data and do not engage in third-party data brokering.
          </p>

          <h2 className="text-2xl font-bold pt-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Data Storage & Security
          </h2>
          <p style={{ color: "var(--text-200)" }}>
            Reasonable technical and organisational measures are used to safeguard information. 
            However, as the platform evolves, data handling practices may change and will be updated accordingly.
          </p>

          <h2 className="text-2xl font-bold pt-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Third-Party Services
          </h2>
          <p style={{ color: "var(--text-200)" }}>We may use trusted third-party tools for:</p>
          <ul className="list-disc list-inside space-y-2" style={{ color: "var(--text-200)" }}>
            <li>Hosting and infrastructure</li>
            <li>Analytics and performance monitoring</li>
            <li>Communications and form handling</li>
          </ul>
          <p style={{ color: "var(--text-300)" }}>
            These providers process data under their own privacy policies.
          </p>

          <h2 className="text-2xl font-bold pt-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Policy Evolution
          </h2>
          <p style={{ color: "var(--text-200)" }}>
            This policy reflects the platform's current operational state and may be updated as features, 
            integrations, and services expand.
          </p>

          <div className="pt-6 border-t" style={{ borderColor: "rgba(176, 176, 176, 0.2)" }}>
            <p style={{ color: "var(--text-300)" }}>
              For privacy enquiries, contact: <a href="mailto:control@machinerymarketed.com" className="text-gradient">control@machinerymarketed.com</a>
            </p>
          </div>
        </div>
      </div>
    </main>
  </div>
);

// Terms of Use Page
const TermsPage = () => (
  <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
    <nav className="glass py-4 sticky top-0 z-50">
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src="https://www.machinerymarketed.com/app-logo.png" alt="Logo" className="h-10 w-10" />
          <span className="font-bold text-xl" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <Link to="/" className="btn-outline text-sm">Back to Home</Link>
      </div>
    </nav>
    
    <main className="container mx-auto px-4 lg:px-8 py-16" data-testid="terms-page">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold mb-8" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
          Terms of Use
        </h1>
        
        <div className="card p-8 space-y-6" style={{ background: "var(--background-200)", borderColor: "rgba(176, 176, 176, 0.15)" }}>
          <p style={{ color: "var(--text-300)" }}>Last updated: February 2026</p>
          
          <p style={{ color: "var(--text-200)" }}>
            By accessing this website or engaging with Machinery Marketed, you agree to these Terms of Use.
          </p>

          <h2 className="text-2xl font-bold pt-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Platform Status
          </h2>
          <p style={{ color: "var(--text-200)" }}>
            Machinery Marketed! is an actively developing platform providing tools and services for machinery 
            dealerships, sales teams, and managers. Features, functionality, and availability may change without notice.
          </p>

          <h2 className="text-2xl font-bold pt-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            No Guarantees
          </h2>
          <p style={{ color: "var(--text-200)" }}>
            All content, tools, and materials are provided "as is" and "as available." We make no warranties regarding:
          </p>
          <ul className="list-disc list-inside space-y-2" style={{ color: "var(--text-200)" }}>
            <li>Accuracy of valuations or appraisals</li>
            <li>Commercial outcomes or sales performance</li>
            <li>Platform uptime or uninterrupted access</li>
          </ul>

          <h2 className="text-2xl font-bold pt-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Professional Judgment
          </h2>
          <p style={{ color: "var(--text-200)" }}>
            Any insights, outputs, or tools provided are intended to support—not replace—professional judgment, 
            internal processes, or independent verification.
          </p>

          <h2 className="text-2xl font-bold pt-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Acceptable Use
          </h2>
          <p style={{ color: "var(--text-200)" }}>You agree not to:</p>
          <ul className="list-disc list-inside space-y-2" style={{ color: "var(--text-200)" }}>
            <li>Misuse, reverse-engineer, or attempt to exploit the platform</li>
            <li>Upload unlawful, misleading, or infringing content</li>
            <li>Interfere with platform security or performance</li>
          </ul>

          <h2 className="text-2xl font-bold pt-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Intellectual Property
          </h2>
          <p style={{ color: "var(--text-200)" }}>
            All branding, concepts, workflows, and original materials remain the intellectual property of 
            Machinery Marketed! unless otherwise stated.
          </p>

          <h2 className="text-2xl font-bold pt-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Changes
          </h2>
          <p style={{ color: "var(--text-200)" }}>
            These terms may be updated as the platform matures. Continued use constitutes acceptance of revised terms.
          </p>
        </div>
      </div>
    </main>
  </div>
);

// Cookies Policy Page
const CookiesPage = () => (
  <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
    <nav className="glass py-4 sticky top-0 z-50">
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src="https://www.machinerymarketed.com/app-logo.png" alt="Logo" className="h-10 w-10" />
          <span className="font-bold text-xl" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <Link to="/" className="btn-outline text-sm">Back to Home</Link>
      </div>
    </nav>
    
    <main className="container mx-auto px-4 lg:px-8 py-16" data-testid="cookies-page">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold mb-8" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
          Cookies Policy
        </h1>
        
        <div className="card p-8 space-y-6" style={{ background: "var(--background-200)", borderColor: "rgba(176, 176, 176, 0.15)" }}>
          <p style={{ color: "var(--text-300)" }}>Last updated: February 2026</p>
          
          <p style={{ color: "var(--text-200)" }}>
            Machinery Marketed! uses cookies and similar technologies to support basic website functionality 
            and understand how visitors interact with the site.
          </p>

          <h2 className="text-2xl font-bold pt-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            What Cookies We Use
          </h2>
          <p style={{ color: "var(--text-200)" }}>Cookies may be used to:</p>
          <ul className="list-disc list-inside space-y-2" style={{ color: "var(--text-200)" }}>
            <li>Enable essential site functions</li>
            <li>Measure traffic and performance trends</li>
            <li>Improve usability and content relevance</li>
          </ul>

          <h2 className="text-2xl font-bold pt-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            What Cookies Do Not Do
          </h2>
          <p style={{ color: "var(--text-200)" }}>Cookies used on this site:</p>
          <ul className="list-disc list-inside space-y-2" style={{ color: "var(--text-200)" }}>
            <li>Do not access personal files</li>
            <li>Do not collect sensitive personal information</li>
            <li>Do not track users across unrelated websites</li>
          </ul>

          <h2 className="text-2xl font-bold pt-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Managing Cookies
          </h2>
          <p style={{ color: "var(--text-200)" }}>
            You can control or disable cookies through your browser settings. Disabling cookies may impact site functionality.
          </p>

          <h2 className="text-2xl font-bold pt-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            Ongoing Review
          </h2>
          <p style={{ color: "var(--text-200)" }}>
            As additional features and integrations are introduced, cookie usage may evolve and this policy will be updated accordingly.
          </p>
        </div>
      </div>
    </main>
  </div>
);

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Main App Component
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/sales-workflow" element={<SalesWorkflowPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/why-choose-us" element={<WhyChooseUsPage />} />
          <Route path="/getting-started" element={<GettingStartedPage />} />
          <Route path="/marketing-distribution" element={<MarketingDistributionPage />} />
          <Route path="/appraisal-to-market" element={<AppraisalToMarketPage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/valuation" element={<ValuationPage />} />
          <Route path="/used-equipment" element={<UsedEquipmentPage />} />
          <Route path="/3-steps" element={<ThreeStepsPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/sitemap" element={<SitemapPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
